import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AkitaPaymentQuery } from '@app/akita/api/payments/state/payment.query';
import { environment } from '@environments/environment';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { map, Subject, Subscription, takeUntil } from 'rxjs';
import { TabbySessionApiResponse } from '../../models/tabby-session.model';
import {
  TabbyActionsService,
  /* TABBY_MAX_ALLOWED_AMOUNT, */
  TABBY_SUPPORTED_CURRENCIES,
} from '../../services/tabby-actions.service';
import { logPaymentInfoCardClicked } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';

const AMOUNT_PRECISION = 2;

@Component({
  selector: 'popsy-tabby-promo',
  templateUrl: './tabby-promo.template.html',
  styleUrls: ['./tabby-promo.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule],
})
export class TabbyPromoComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() public amount?: number | null;
  @Input() public currency?: string | null;
  @Input() public source?: 'product' | 'cart' | null;

  public isTabbySupported: boolean;
  public installmentCount?: number | null;

  private tabbySessionSubscription?: Subscription | null;
  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly tabbyActionsService: TabbyActionsService,
    private readonly akitaPaymentQuery: AkitaPaymentQuery,
    private readonly translateService: TranslocoService,
    private readonly analytics: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.isTabbySupported = false;
    this.monitorTabbySession();
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.listingId || changes?.currency || changes?.amount || changes?.source) {
      this.monitorTabbySession();
      this.displayTabbyPromo();
    }
  }

  public ngAfterViewInit(): void {
    this.displayTabbyPromo();
  }

  private parseInstallmentsCount(
    response?: TabbySessionApiResponse | null
  ): number | null | undefined {
    const installments = response?.configuration?.availableProducts?.installments;
    if (installments && installments?.length > 0) {
      return installments[0].installmentsCount || undefined;
    } else {
      return undefined;
    }
  }

  private monitorTabbySession(): void {
    if (this.tabbySessionSubscription) {
      this.tabbySessionSubscription.unsubscribe();
    }

    this.tabbySessionSubscription = this.akitaPaymentQuery
      .selectTabbySession([])
      .pipe(map(this.parseInstallmentsCount.bind(this)), takeUntil(this.destroySubject))
      .subscribe({
        next: (count?: number | null) => {
          this.installmentCount = count || undefined;
          this.displayTabbyPromo();
        },
        error: () => {},
      });
  }

  private displayTabbyPromo(): void {
    this.isTabbySupported = TABBY_SUPPORTED_CURRENCIES.includes(
      `${this.currency || 'SAR'}`.toUpperCase()
    ); /* &&
      (this.amount || 0) < TABBY_MAX_ALLOWED_AMOUNT; */

    if (this.isTabbySupported && this.amount) {
      let lang = 'en';
      if (`${this.translateService.getActiveLang() || ''}`.toLowerCase() === 'ar') {
        lang = 'ar';
      }

      this.subscriptions.add(
        this.tabbyActionsService
          .displayTabbyPromo({
            selector: '#TabbyPromoContainer',
            currency: this.currency || 'SAR',
            price: this.amount.toFixed(AMOUNT_PRECISION) || '1000',
            installmentsCount: this.installmentCount || undefined,
            lang: lang,
            source: this.source || undefined,
            api_key:
              this.akitaPaymentQuery.tabbyPublicKey ||
              environment.tabby.pubKey ||
              undefined,
          })
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: () => {},
            error: () => {},
          })
      );
    }
  }

  public logClickEvent(): void {
    const trigger =
      this.source === 'product' ? 'Tabby-product-page' : 'Tabby-checkout-page';
    logPaymentInfoCardClicked(this.analytics.logEventWrapper, trigger);
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.tabbySessionSubscription) {
      this.tabbySessionSubscription.unsubscribe();
    }
  }
}
